// DomainChecksDashboard.js - 8 Essential GEO Domain Checks
// Dec 31 2025 - Focused on access, discovery, and trust for LLM citation

import React, { useState, useEffect } from 'react';

// Load Work Sans font
if (typeof document !== 'undefined') {
  const fontLink = document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
  fontLink.rel = 'stylesheet';
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    document.head.appendChild(fontLink);
  }
}

const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  accent: '#f4a261',
  grey: '#e76f51',
  green: '#01796f'
};

function DomainChecksDashboard({ campaignId, urls, apiBaseUrl }) {
  const [loading, setLoading] = useState(false);
  const [checkResults, setCheckResults] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);

  // Extract domain from campaign URLs
  const domain = urls && urls.length > 0 ? new URL(urls[0]).origin : null;

  useEffect(() => {
    if (domain) {
      loadCachedResults();
    }
  }, [domain]);

  const loadCachedResults = async () => {
    if (!domain) return;

    try {
      const response = await fetch(`${apiBaseUrl}/modules/domain-checks?domain=${encodeURIComponent(domain)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.results) {
          setCheckResults(data.results);
          setLastChecked(data.lastChecked);
        }
      }
    } catch (err) {
      console.error('Error loading cached results:', err);
    }
  };

  const runDomainChecks = async () => {
    if (!domain) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/modules/domain-checks/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, campaignId })
      });

      if (!response.ok) {
        throw new Error('Failed to run domain checks');
      }

      const data = await response.json();
      setCheckResults(data.results);
      setLastChecked(new Date().toISOString());
    } catch (err) {
      console.error('Error running checks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = () => {
    if (!checkResults) return { passed: 0, total: 0 };
    
    const checks = Object.values(checkResults);
    const passed = checks.filter(c => c.status === 'pass').length;
    const total = checks.length;
    
    return { passed, total };
  };

  const getChecksByCategory = () => {
    if (!checkResults) return {};

    return {
      discovery: [
        checkResults.llmstxt,
        checkResults.robotstxt,
        checkResults.sitemap,
        checkResults.rss
      ].filter(Boolean),
      trust: [
        checkResults.https,
        checkResults.ssl,
        checkResults.crawlable,
        checkResults.jsRendering,
        checkResults.domainAge
      ].filter(Boolean)
    };
  };

  const score = calculateScore();
  const scorePercent = score.total > 0 ? Math.round((score.passed / score.total) * 100) : 0;
  const categories = getChecksByCategory();

  return (
    <div style={{ 
      fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', padding: '32px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: colors.primary, margin: 0, marginBottom: '8px' }}>
          Domain-Level Checks
        </h1>
        <p style={{ fontSize: '16px', color: '#6b7280', margin: 0, marginBottom: '16px' }}>
          Essential configurations that affect how AI engines discover, access, and trust your domain
        </p>

        {domain && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: colors.secondary 
              }} />
              <span style={{ fontSize: '15px', fontWeight: '600', color: colors.primary }}>
                {domain}
              </span>
            </div>
            {lastChecked && (
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Last checked: {new Date(lastChecked).toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Summary Scorecard */}
      {checkResults && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.07)', 
          padding: '32px',
          marginBottom: '24px',
          border: `3px solid ${scorePercent >= 80 ? colors.green : scorePercent >= 60 ? colors.tertiary : colors.grey}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                GEO Readiness Score
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                <span style={{ 
                  fontSize: '64px', 
                  fontWeight: '800', 
                  color: scorePercent >= 80 ? colors.green : scorePercent >= 60 ? colors.tertiary : colors.grey,
                  lineHeight: 1
                }}>
                  {score.passed}
                </span>
                <span style={{ fontSize: '32px', fontWeight: '600', color: '#6b7280' }}>
                  / {score.total}
                </span>
              </div>
              <div style={{ fontSize: '16px', color: '#6b7280', marginTop: '8px' }}>
                {scorePercent >= 80 ? '🟢 Excellent' : scorePercent >= 60 ? '🟡 Good' : scorePercent >= 40 ? '🟠 Needs Work' : '🔴 Critical Issues'} - {scorePercent}% checks passed
              </div>
            </div>

            <button
              onClick={runDomainChecks}
              disabled={loading || !domain}
              style={{
                padding: '14px 28px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '15px',
                color: 'white',
                backgroundColor: loading ? '#9ca3af' : colors.secondary,
                border: 'none',
                cursor: loading || !domain ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                fontFamily: "'Work Sans', sans-serif"
              }}
            >
              {loading ? 'Running Checks...' : '🔄 Re-run Checks'}
            </button>
          </div>
        </div>
      )}

      {/* Run Checks Button (if no results) */}
      {!checkResults && !loading && domain && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: colors.primary, marginBottom: '8px' }}>
            Ready to Check Your Domain
          </h3>
          <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>
            Run 8 essential checks to verify your domain is optimized for AI engine discovery and citation
          </p>
          <button
            onClick={runDomainChecks}
            style={{
              padding: '14px 28px',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '15px',
              color: 'white',
              backgroundColor: colors.secondary,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              fontFamily: "'Work Sans', sans-serif"
            }}
          >
            🚀 Run Domain Checks
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{ backgroundColor: '#fee2e2', border: '2px solid #ef4444', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <p style={{ color: '#dc2626', fontWeight: '600', margin: 0 }}>
            ⚠️ Error: {error}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', padding: '48px', textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #e5e7eb', 
            borderTop: '4px solid ' + colors.secondary, 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
            Running 8 essential domain checks...
          </p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Check Results by Category - 2 Column Layout */}
      {checkResults && !loading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* LEFT COLUMN - Discovery & Access */}
          <CategorySection 
            title="Discovery & Access"
            description="Help AI engines find and crawl your content"
            checks={categories.discovery}
            colors={colors}
          />

          {/* RIGHT COLUMN - Trust & Readability */}
          <CategorySection 
            title="Trust & Readability"
            description="Build trust and ensure AI bots can read your content"
            checks={categories.trust}
            colors={colors}
          />
        </div>
      )}
    </div>
  );
}

// Category Section Component
function CategorySection({ title, description, checks, colors }) {
  if (!checks || checks.length === 0) return null;

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
      <div style={{ padding: '24px', borderBottom: '2px solid #e5e7eb' }}>
        <h3 style={{ fontSize: '20px', fontWeight: '700', color: colors.primary, margin: 0, marginBottom: '4px' }}>
          {title}
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
          {description}
        </p>
      </div>

      <div style={{ padding: '16px' }}>
        <div style={{ display: 'grid', gap: '12px' }}>
          {checks.map((check, index) => (
            <CheckCard key={index} check={check} colors={colors} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Individual Check Card Component
function CheckCard({ check, colors }) {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status) => {
    if (status === 'pass') return colors.green;
    if (status === 'warning') return colors.tertiary;
    return colors.grey;
  };

  const getStatusIcon = (status) => {
    if (status === 'pass') return '✓';
    if (status === 'warning') return '⚠';
    return '✗';
  };

  const getStatusBg = (status) => {
    if (status === 'pass') return '#e0f5f3';
    if (status === 'warning') return '#fef3c7';
    return '#fee2e2';
  };

  return (
    <div style={{ 
      border: '2px solid #e5e7eb', 
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'all 0.2s',
      borderColor: expanded ? getStatusColor(check.status) : '#e5e7eb'
    }}>
      {/* Check Header - Clickable */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '16px',
          backgroundColor: '#f9fafb',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          transition: 'background-color 0.2s',
          fontFamily: "'Work Sans', sans-serif"
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
      >
        {/* Status Badge */}
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: getStatusBg(check.status),
          color: getStatusColor(check.status),
          fontSize: '16px',
          fontWeight: '700',
          flexShrink: 0
        }}>
          {getStatusIcon(check.status)}
        </div>

        {/* Check Info */}
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <span style={{ fontSize: '15px', fontWeight: '600', color: colors.primary }}>
              {check.name}
            </span>
            {check.priority === 'critical' && (
              <span style={{ 
                fontSize: '10px', 
                fontWeight: '700', 
                padding: '2px 6px', 
                borderRadius: '4px', 
                backgroundColor: '#fee2e2', 
                color: '#dc2626',
                textTransform: 'uppercase'
              }}>
                CRITICAL
              </span>
            )}
            {check.priority === 'important' && (
              <span style={{ 
                fontSize: '10px', 
                fontWeight: '700', 
                padding: '2px 6px', 
                borderRadius: '4px', 
                backgroundColor: '#fef3c7', 
                color: '#d97706',
                textTransform: 'uppercase'
              }}>
                IMPORTANT
              </span>
            )}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {check.message}
          </div>
        </div>

        {/* Expand Arrow */}
        <div style={{ 
          fontSize: '18px', 
          color: '#6b7280',
          transition: 'transform 0.2s',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
        }}>
          ▼
        </div>
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div style={{ padding: '16px', backgroundColor: 'white', borderTop: '1px solid #e5e7eb' }}>
          {/* Why It Matters */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Why It Matters for GEO
            </div>
            <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
              {check.explanation}
            </div>
          </div>

          {/* Fix Instructions (if failed) */}
          {check.status !== 'pass' && check.fixInstructions && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#eff6ff', 
              border: '2px solid #3b82f6',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af', marginBottom: '6px' }}>
                🔧 How to Fix
              </div>
              <div style={{ fontSize: '14px', color: '#1e3a8a', lineHeight: '1.6' }}>
                {check.fixInstructions}
              </div>
            </div>
          )}

          {/* View File Link (if applicable) */}
          {check.url && (
            <div style={{ marginTop: '12px' }}>
              <a
                href={check.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: colors.secondary,
                  textDecoration: 'none',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                View file →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DomainChecksDashboard;
